import React, {useEffect, useState} from 'react';
import styled, {useTheme} from 'styled-components/native';
import {Platform} from 'react-native';
import {hdp, wdp} from '../../../utils/responsive';
import {FontItem} from '../../../interfaces/screens/fontChanger';
import TextGeneric from '../../../components/textGeneric';
import {tr} from '../../../translations';
import {Slider} from '@miblanchard/react-native-slider';
import {NativeViewGestureHandler} from 'react-native-gesture-handler';
import {updateAppFont} from './actions';
import {store} from '../../../redux/store';
import {AuthState} from '../../../interfaces/reducers/auth';
import lightTheme from '../../../theme/lightTheme';
import {getFontFamily} from '../../../utils/helpers';
import CustomSwitch from '../../../components/customSwitch';

const isWeb = Platform.OS === 'web';

const Container = styled.View`
  width: 100%;
  flex: 1;
  margin: auto;
  padding-left: 2%;
  padding-right: 2%;
  ${isWeb &&
  `
    height: 100%;
    min-height: 500px;
    overflow: scroll;
  `}
`;

const CardContent = styled.View`
  border-radius: 15px;
  text-align: center;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  min-height: ${hdp(20)}px;
  background-color: ${({theme}) => theme.fontChange.card};
  ${isWeb &&
  `
    margin: 0 6%;
  `}
`;

const SliderCard = styled.View`
  border-radius: 30px;
  padding-horizontal: 6%;
  padding-vertical: 4%;
  margin-top: 4%;
`;

const MainText = styled(TextGeneric)`
  color: ${({theme}) => theme.text.dark};
  text-align: center;
  padding: ${hdp(4)}px;
`;

const Title = styled(TextGeneric)`
  color: ${({theme}) => theme.text.dark};
  text-align: center;
  padding-horizontal: 3%;
  padding-vertical: 6%;
  font-size: ${({theme}) => theme.text.s6}px;
`;

const Label = styled(TextGeneric)`
  color: ${({theme}) => theme.text.dark};
  font-size: ${({theme}) => theme.text.s7}px;
`;

const MainView = styled.View`
  padding-horizontal: 6%;
  ${isWeb &&
  `
    width: 100%;
    max-width: 600px;
    margin: 0 auto;
  `}
`;

const RowView = styled.View`
  flex-direction: row;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  padding-top: 8%;
`;

const TrackMarkContainer = styled.View<{index: number}>`
  align-items: ${({index}) =>
    index === 0 ? 'flex-start' : index === 3 ? 'flex-end' : 'center'};
  justify-content: center;
  width: ${({theme}) => theme.text.s4}px;
  aspect-ratio: 1;
`;

const TrackMark = styled.View`
  height: ${hdp(2)}px;
  width: 1px;
  background-color: ${({theme}) => theme.text.dark};
`;

const TrackMarkLabel = styled(TextGeneric)`
  color: ${({theme}) => theme.text.dark};
  text-align: center;
  padding-horizontal: 3%;
  padding-vertical: 6%;
  font-size: ${({theme, textSize}) => textSize || theme.text.s8}px;
  position: absolute;
  bottom: -${hdp(5)}px;
  left: -${({theme, textSize}) => (textSize || theme.text.s8) / 2}px;
  width: ${wdp(10)}px;
`;

const SliderWrapper = styled.View`
  ${isWeb &&
  `
    margin: 0 6%;
  `}
`;

const FontChanger = () => {
  const {config} = store.getState().auth as AuthState;
  const {fontWeight, item} = config?.font || {};
  const theme = useTheme();
  const [isEnabled, setFontWeightEnabled] = useState(fontWeight === 'bold');
  const sliderOptions: Array<FontItem> = [
    {
      value: 0,
      label: '14',
      textSize: theme.text.s9,
    },
    {
      value: 1,
      label: '17',
      textSize: theme.text.s7,
    },
    {
      value: 2,
      label: '20',
      textSize: theme.text.s55,
    },
    {
      value: 3,
      label: '24',
      textSize: theme.text.s5,
    },
  ];
  const [selectedItem, setSelectedItem] = useState<FontItem>(
    item || {
      value: 2,
      label: '20',
      textSize: lightTheme.text.s55,
    },
  );

  useEffect(() => {
    const temp = {
      item: selectedItem,
      fontSize: selectedItem?.textSize,
      fontWeight: isEnabled ? 'bold' : 'normal',
    };
    updateAppFont(temp);
  }, [selectedItem, isEnabled]);

  const _renderTrackMarkComponent = (index: number) => {
    return (
      <TrackMarkContainer index={index}>
        {(selectedItem?.value || 0) !== index && <TrackMark />}
        <TrackMarkLabel textSize={sliderOptions[index].textSize}>
          {sliderOptions[index].label}
        </TrackMarkLabel>
      </TrackMarkContainer>
    );
  };

  const renderSlider = () => (
    <SliderWrapper>
      <Slider
        value={selectedItem?.value || 0}
        maximumValue={3}
        minimumValue={0}
        step={1}
        trackMarks={[0, 1, 2, 3]}
        maximumTrackTintColor={theme.text.dark + '22'}
        thumbStyle={{
          width: theme.text.s5,
          height: theme.text.s5,
        }}
        minimumTrackTintColor={'transparent'}
        thumbTintColor={theme.text.dark}
        renderTrackMarkComponent={_renderTrackMarkComponent}
        onValueChange={selected =>
          //@ts-ignore
          setSelectedItem(sliderOptions[selected])
        }
        animateTransitions
      />
    </SliderWrapper>
  );

  return (
    <Container>
      <MainView>
        <Title>{tr('fontChanger.title')}</Title>
        <CardContent>
          <MainText
            style={[
              isWeb && {
                fontWeight: isEnabled ? 'bold' : 400,
              },
              {
                fontSize: selectedItem?.textSize,
                fontFamily: isEnabled
                  ? getFontFamily().bold
                  : getFontFamily().regular,
              },
            ]}>
            {tr('fontChanger.randomText')}
          </MainText>
        </CardContent>
        {isWeb ? (
          renderSlider()
        ) : (
          <NativeViewGestureHandler disallowInterruption={true}>
            <SliderCard>{renderSlider()}</SliderCard>
          </NativeViewGestureHandler>
        )}
        <SliderCard>
          <RowView>
            <Label>{tr('fontChanger.boldFont')}</Label>
            <CustomSwitch
              isEnabled={isEnabled}
              onToggle={setFontWeightEnabled}
              size="medium"
              activeColor={theme.switch.trackOn}
              inactiveColor={theme.switch.trackOff}
            />
          </RowView>
        </SliderCard>
      </MainView>
    </Container>
  );
};

export default FontChanger;
